import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
// All exports in this file use ExcelJS, not the free "xlsx" (SheetJS
// Community Edition) package — SheetJS CE can't write cell colors/fills or
// borders into the output file at all (that's a SheetJS Pro-only feature).
import ExcelJS from 'exceljs';
import DOMPurify from 'dompurify';
import '../api';

// Paleta partilhada pelas exportações Excel com cores (Gestores, Clientes).
const XLSX_HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F5E8C' } };
const XLSX_HEADER_FONT = { bold: true, color: { argb: 'FFFFFFFF' } };
const XLSX_SECTION_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCE6F1' } };
const XLSX_STRIPE_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF3F8' } };
const XLSX_THIN_BORDER = { style: 'thin', color: { argb: 'FFD0D7E2' } };

// Borda fina preta em todas as células com dados, em todos os Excel
// exportados — pedido explicitamente para facilitar a leitura por parte dos
// trabalhadores. Aplica-se sempre no fim, depois de todas as linhas de uma
// folha estarem inseridas, para não ser sobrescrita por outros estilos
// (fill/font) entretanto aplicados às mesmas células.
const XLSX_BLACK_BORDER = { style: 'thin', color: { argb: 'FF000000' } };
const XLSX_ALL_BORDERS = { top: XLSX_BLACK_BORDER, left: XLSX_BLACK_BORDER, bottom: XLSX_BLACK_BORDER, right: XLSX_BLACK_BORDER };
const applyBordersToSheet = (sheet) => {
  sheet.eachRow((row) => {
    row.eachCell((cell) => { cell.border = XLSX_ALL_BORDERS; });
  });
};

const normalizeKey = (value) => (value || '').toString().trim().toLowerCase();

// Ano plausível para qualquer data de negócio nesta aplicação — nada aqui é
// anterior a 1990 nem depois de 2100. Serve de última linha de defesa contra
// qualquer "fantasma" de data corrompida (ex: um valor perto de 1899/1900,
// sintoma clássico de um serial de data a 0 mal interpretado): mesmo que uma
// string inesperada consiga ser parseada por new Date(), se o ano não bater
// certo o valor é tratado como se não existisse, nunca mostrado ao
// utilizador nem escrito num Excel.
const isPlausibleYear = (yyyy) => yyyy >= 1990 && yyyy <= 2100;

const toDateOnly = (value) => {
  if (value === 0) return '';
  if (!value) return '';
  const raw = value.toString();
  const rawTrim = raw.trim();
  if (!rawTrim || rawTrim === '0' || rawTrim === '0000-00-00' || rawTrim === '0000-00-00 00:00:00') return '';
  const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isPlausibleYear(Number(isoMatch[1].slice(0, 4))) ? isoMatch[1] : '';
  const ptMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (ptMatch) {
    const [, dd, mm, yyyy] = ptMatch;
    return isPlausibleYear(Number(yyyy)) ? `${yyyy}-${mm}-${dd}` : '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const yyyy = date.getFullYear();
  if (!isPlausibleYear(yyyy)) return '';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const toDatePt = (value) => {
  const dateOnly = toDateOnly(value);
  if (!dateOnly) return '—';
  const [yyyy, mm, dd] = dateOnly.split('-');
  return `${dd}/${mm}/${yyyy}`;
};

// Parses monetary strings from the WordPress meta into a plain number.
// Values in the real data are inconsistent ("580", "1.390,00€ + iva",
// "350,00") — strip anything that isn't a digit/separator, then normalize
// PT-style thousands "." and decimal "," to a parseable form. Returns null
// (not 0) when nothing numeric survives, so callers can tell "no value" from
// "zero" and skip it from sums rather than silently treating it as 0.
const parseMoeda = (value) => {
  if (value === null || value === undefined) return null;
  const cleaned = value.toString().trim().replace(/[^\d,.-]/g, '');
  if (!cleaned) return null;
  let normalized = cleaned;
  if (normalized.includes(',') && normalized.includes('.')) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else if (normalized.includes(',')) {
    normalized = normalized.replace(',', '.');
  }
  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : null;
};

const formatMoeda = (value) => {
  const num = parseMoeda(value);
  if (num === null) return '—';
  return num.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' });
};

const cleanReportText = (value) => {
  const raw = (value || '').toString().trim();
  if (!raw) return '';
  if (/^\[\s*\]$/.test(raw)) return '';
  return raw;
};

// Plain-text version of a descritivo's HTML, for Excel cells (which can't
// render markup) — strips tags and collapses whitespace/line breaks.
const stripHtml = (html) => (html || '')
  .toString()
  .replace(/<[^>]*>/g, ' ')
  .replace(/&nbsp;/gi, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const toBool = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  const normalized = (value || '').toString().trim().toLowerCase();
  return normalized === '1' || normalized === 'true' || normalized === 'sim' || normalized === 'yes';
};

const asText = (...values) => {
  for (const value of values) {
    const text = cleanReportText(value);
    if (text) return text;
  }
  return '';
};

const cleanManagerName = (value) => {
  const raw = cleanReportText(value);
  if (!raw) return '—';

  const parenMatch = raw.match(/\(([^)]+)\)/);
  if (parenMatch && parenMatch[1]) return parenMatch[1].trim();

  if (raw.includes('/')) return raw.split('/')[0].trim();
  if (raw.includes(',')) return raw.split(',')[0].trim();
  if (raw.includes('|')) return raw.split('|')[0].trim();

  return raw;
};

const hasKeyword = (text, pattern) => pattern.test((text || '').toString().toLowerCase());

const normalizeRole = (role) => {
  const raw = (role || '').toString().trim().toLowerCase();
  if (raw === 'administrator' || raw === 'administrador' || raw === 'admin') return 'admin';
  if (raw === 'contributor' || raw === 'contribuidor') return 'contributor';
  if (raw === 'editor' || raw === 'editora') return 'editor';
  return raw || 'editor';
};

const groupItemsByDate = (items, getDateValue) => {
  const groups = new Map();

  items.forEach((item) => {
    const dateKey = toDateOnly(getDateValue(item)) || 'sem-data';
    if (!groups.has(dateKey)) groups.set(dateKey, []);
    groups.get(dateKey).push(item);
  });

  return [...groups.entries()]
    .sort(([a], [b]) => {
      if (a === 'sem-data') return 1;
      if (b === 'sem-data') return -1;
      return b.localeCompare(a);
    })
    .map(([dateKey, groupedItems]) => ({
      dateKey,
      dateLabel: dateKey === 'sem-data' ? 'Sem data' : toDatePt(dateKey),
      items: groupedItems
    }));
};

const MESES_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

// Groups items by calendar month (year-month), oldest month first, with each
// group's items sorted chronologically by their exact date — used by
// "Contactos a Efetuar" so each month gets its own heading (e.g. "Janeiro de
// 2026") instead of one heading per exact day.
const groupItemsByMonth = (items, getDateValue) => {
  const groups = new Map();

  items.forEach((item) => {
    const dateOnly = toDateOnly(getDateValue(item));
    const monthKey = dateOnly ? dateOnly.slice(0, 7) : 'sem-data';
    if (!groups.has(monthKey)) groups.set(monthKey, []);
    groups.get(monthKey).push(item);
  });

  return [...groups.entries()]
    .sort(([a], [b]) => {
      if (a === 'sem-data') return 1;
      if (b === 'sem-data') return -1;
      return a.localeCompare(b);
    })
    .map(([monthKey, groupedItems]) => {
      const items = [...groupedItems].sort((a, b) => {
        const da = toDateOnly(getDateValue(a));
        const db = toDateOnly(getDateValue(b));
        return da.localeCompare(db);
      });

      const monthLabel = monthKey === 'sem-data'
        ? 'Sem data'
        : `${MESES_PT[Number(monthKey.slice(5, 7)) - 1]} de ${monthKey.slice(0, 4)}`;

      return { monthKey, monthLabel, items };
    });
};

// Categorias reais confirmadas em wp_postmeta (meta_key "tipo_de_contacto") —
// qualquer valor fora desta lista (vazio, ou um valor antigo/inesperado) cai
// em "Outro/Não especificado" em vez de ser ignorado silenciosamente.
const TIPOS_CONTACTO = ['Telefónico', 'Email', 'Reunião', 'Visita'];
const TIPO_CONTACTO_OUTRO = 'Outro/Não especificado';

// "Tipo de Cliente" é derivado (não existe como campo nos dados — ver
// getTipoCliente no componente), sempre um destes três valores.
const TIPOS_CLIENTE = ['Não Cliente', 'Cliente', 'Novo Cliente'];

// Ficha do Cliente — "Orçamentos e Estado Comercial": os 3 estados do
// modelo clássico não existem como valores literais nos dados; são
// derivados dos 5 valores reais confirmados em estado_da_proposta.
// "Escolha uma opção" (placeholder do dropdown, nunca escolhido de facto)
// fica de fora das 3 secções.
const ESTADO_PROPOSTA_BUCKET = {
  'Adjudicada': 'Aprovados',
  'Enviada': 'Em Análise',
  'A Orçamentar': 'Em Análise',
  'Não Adjudicada': 'Rejeitados',
  'Desinteresse': 'Rejeitados'
};
const ORCAMENTO_BUCKETS = ['Em Análise', 'Aprovados', 'Rejeitados'];

// Métricas do resumo (global e por gestor) — mesma função usada nos dois
// sítios para garantir que os números batem certo entre si.
const buildResumoMetrics = (items) => {
  const porTipoContacto = {};
  TIPOS_CONTACTO.forEach((tipo) => { porTipoContacto[tipo] = 0; });
  porTipoContacto[TIPO_CONTACTO_OUTRO] = 0;

  const porTipoCliente = {};
  TIPOS_CLIENTE.forEach((tipo) => { porTipoCliente[tipo] = 0; });

  const porLocalidade = {};

  const clientesUnicos = new Set();

  items.forEach((item) => {
    const tipoContacto = TIPOS_CONTACTO.includes(item.tipoContacto) ? item.tipoContacto : TIPO_CONTACTO_OUTRO;
    porTipoContacto[tipoContacto] += 1;
    porTipoCliente[item.tipoCliente] = (porTipoCliente[item.tipoCliente] || 0) + 1;
    const localidade = item.localidade || 'Sem localidade';
    porLocalidade[localidade] = (porLocalidade[localidade] || 0) + 1;
    // "Total de Clientes" conta clientes reais (exclui "Não Cliente", que por
    // definição não corresponde a nenhum cliente) — um cliente com várias
    // fichas só conta uma vez.
    if (item.tipoCliente !== 'Não Cliente') {
      clientesUnicos.add(item.clienteId || item.clienteNome);
    }
  });

  return {
    total: items.length,
    totalClientes: clientesUnicos.size,
    agendados: items.filter((item) => item.sinalAgendado).length,
    followUp: items.filter((item) => item.sinalFollowUp).length,
    reforcos: items.filter((item) => item.sinalReforco).length,
    realizados: items.filter((item) => item.sinalRealizado).length,
    novos: items.filter((item) => item.sinalNovo).length,
    desmarcados: items.filter((item) => item.sinalDesmarcado).length,
    porTipoContacto,
    porTipoCliente,
    porLocalidade
  };
};

export default function Relatorios({ user = null }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tipo = searchParams.get('tipo') || 'propostas-adjudicadas';

  const [fichas, setFichas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [comerciais, setComerciais] = useState([]);
  const [propostasMeta, setPropostasMeta] = useState({});
  const [loading, setLoading] = useState(true);

  const [clienteAdj, setClienteAdj] = useState('Todos');
  const [clienteProp, setClienteProp] = useState('Todos');
  const [dataContactos, setDataContactos] = useState('');
  const [gestor, setGestor] = useState('Todos');
  const [clienteGC, setClienteGC] = useState('Todos');
  const [tipoContactoGC, setTipoContactoGC] = useState('Todos');
  const [localidadeGC, setLocalidadeGC] = useState('Todos');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  const [submittedClienteAdj, setSubmittedClienteAdj] = useState('Todos');
  const [submittedClienteProp, setSubmittedClienteProp] = useState('Todos');
  const [submittedDataContactos, setSubmittedDataContactos] = useState('');
  const [submittedGC, setSubmittedGC] = useState({ gestor: 'Todos', cliente: 'Todos', tipoContacto: 'Todos', localidade: 'Todos', dataInicio: '', dataFim: '' });
  const [showContactos, setShowContactos] = useState(false);
  const [showGestorClientes, setShowGestorClientes] = useState(false);
  const [expandedMesesPropostas, setExpandedMesesPropostas] = useState({});
  const [expandedMesesAdjudicadas, setExpandedMesesAdjudicadas] = useState({});
  const [expandedDatasContactos, setExpandedDatasContactos] = useState({});
  const [expandedGestoresClientes, setExpandedGestoresClientes] = useState({});
  const [expandedDatasGestorClientes, setExpandedDatasGestorClientes] = useState({});

  const [pesquisaPropostas, setPesquisaPropostas] = useState('');
  const [pesquisaAdjudicadas, setPesquisaAdjudicadas] = useState('');

  const [clienteFicha, setClienteFicha] = useState('Todos');
  const [submittedClienteFicha, setSubmittedClienteFicha] = useState('Todos');
  const [showClienteFicha, setShowClienteFicha] = useState(false);

  // Permite abrir a Ficha de um cliente já pré-selecionada a partir de fora
  // (ex: o botão "Ver Ficha" na listagem de Clientes), via
  // /relatorios?tipo=clientes&cliente=<nome>.
  useEffect(() => {
    if (tipo !== 'clientes') return;
    const clienteParam = searchParams.get('cliente');
    if (!clienteParam) return;
    setClienteFicha(clienteParam);
    setSubmittedClienteFicha(clienteParam);
    setShowClienteFicha(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só deve correr quando o parâmetro de URL muda, não a cada render
  }, [tipo, searchParams]);

  useEffect(() => {
    let mounted = true;
    const fetchAll = () => Promise.all([
      axios.get('/api/fichas').catch(() => ({ data: [] })),
      axios.get('/api/clientes').catch(() => ({ data: [] })),
      axios.get('/api/comerciais').catch(() => ({ data: [] })),
      axios.get('/api/fichas/propostas-meta').catch(() => ({ data: {} }))
    ]).then(([fRes, cRes, mRes, pmRes]) => {
      if (!mounted) return;
      setFichas(
        (Array.isArray(fRes.data) ? fRes.data : [])
          .filter((f) => (f.post_status || '').toString().toLowerCase() === 'publish')
      );
      setClientes(Array.isArray(cRes.data) ? cRes.data : []);
      setComerciais(Array.isArray(mRes.data) ? mRes.data : []);
      setPropostasMeta(pmRes.data && typeof pmRes.data === 'object' ? pmRes.data : {});
      setLoading(false);
    }).catch(() => {
      if (!mounted) return;
      setFichas([]);
      setClientes([]);
      setComerciais([]);
      setPropostasMeta({});
      setLoading(false);
    });

    // initial fetch
    fetchAll();
    // poll every 60s to keep reports updated
    const intervalId = setInterval(fetchAll, 60 * 1000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, []);

  const clienteByAnyKey = useMemo(() => {
    const map = new Map();
    clientes.forEach((cliente) => {
      [
        cliente.id,
        cliente.legacy_id,
        cliente.client_legacy_id,
        cliente.client_id,
        cliente.cliente_id,
        cliente.denominacao_fiscal,
        cliente.nome,
        cliente.client_name,
        cliente.cliente,
        cliente.client,
        cliente.nome_cliente,
        cliente.cliente_nome,
        cliente.nomeCliente,
        cliente.contacto_empresa,
        cliente.pessoa_contacto_nome
      ]
        .filter((value) => value !== undefined && value !== null && value !== '')
        .forEach((value) => map.set(normalizeKey(value), cliente));
    });
    return map;
  }, [clientes]);

  const getClienteRaw = (ficha) => ficha.cliente || ficha.cliente_nome || ficha.clienteNome || ficha.nome_cliente || ficha.nomeCliente || ficha.client_name || ficha.client || ficha.denominacao_fiscal || ficha.client_legacy_id || ficha.cliente_legacy_id || ficha.client_id || ficha.cliente_id || ficha.nome || ficha.title || ficha.post_title;

  const getClienteNome = (ficha) => {
    const raw = getClienteRaw(ficha);
    if (raw === undefined || raw === null || raw === '') return '—';
    const mapped = clienteByAnyKey.get(normalizeKey(raw));
    const nome = mapped?.denominacao_fiscal || mapped?.nome || raw;
    return nome.toString().trim() || '—';
  };

  const getClienteId = (ficha) => {
    const raw = getClienteRaw(ficha);
    const mapped = clienteByAnyKey.get(normalizeKey(raw));
    if (!mapped && raw) {
      const byName = clientes.find((cliente) => {
        const nome = (cliente.denominacao_fiscal || cliente.nome || cliente.client_name || '').toString().trim().toLowerCase();
        return nome && nome === raw.toString().trim().toLowerCase();
      });
      return byName?.id || byName?.legacy_id || null;
    }
    return mapped?.id || mapped?.legacy_id || null;
  };

  const getClienteLocalidade = (ficha) => {
    const raw = getClienteRaw(ficha);
    const mapped = clienteByAnyKey.get(normalizeKey(raw));
    return asText(mapped?.localidade) || 'Sem localidade';
  };

  // "Tipo de Cliente" não existe como campo em lado nenhum dos dados
  // (confirmado diretamente na BD — não há equivalente no WordPress nem
  // nesta app), por isso é derivado aqui: uma ficha sem cliente resolvido é
  // "Não Cliente"; a ficha cronologicamente mais antiga de cada cliente é
  // "Novo Cliente" (primeiro contacto registado); todas as seguintes desse
  // mesmo cliente são "Cliente". Usa `fichas` (não `visibleFichas`) para que
  // a comparação de "primeira data" não dependa de quais fichas o utilizador
  // atual tem permissão para ver.
  const clientePrimeiraDataMap = useMemo(() => {
    const map = new Map();
    fichas.forEach((ficha) => {
      const clienteId = getClienteId(ficha);
      if (!clienteId) return;
      const data = toDateOnly(ficha.data_contacto || ficha.post_date || ficha.created_at || ficha.updated_at);
      if (!data) return;
      const atual = map.get(clienteId);
      if (!atual || data < atual) map.set(clienteId, data);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getClienteId is a plain closure over clientes/clienteByAnyKey; only `fichas` actually changes what this computes
  }, [fichas]);

  const getTipoCliente = (ficha, dataContactoStr) => {
    const clienteId = getClienteId(ficha);
    if (!clienteId) return 'Não Cliente';
    const primeira = clientePrimeiraDataMap.get(clienteId);
    if (primeira && dataContactoStr && dataContactoStr === primeira) return 'Novo Cliente';
    return 'Cliente';
  };

  // "Primeiro a escrever, ganha" (em vez de sobrescrever sempre): o backend
  // já filtra as entradas "legacy" (fallback a partir de author/comercial_id
  // em `clients` sem utilizador real) que colidam com o id de um utilizador
  // real, mas mesmo que uma entrada destas apareça, não deve conseguir
  // substituir o nome verdadeiro de um gestor já mapeado por um valor em
  // bruto (ex: "8" em vez de "Beatriz Cardoso") — foi exatamente isto que
  // escondia os sumários de vários gestores reais.
  const gestorMap = useMemo(() => {
    const map = new Map();
    comerciais.forEach((item) => {
      const label = cleanManagerName(item.label || item.display_name || item.username || item.value || item.name || item.displayName || '');
      if (!label) return;
      [item.value, item.username, item.id, label, item.label, item.display_name, item.name, item.displayName, item.user_nicename]
        .filter((value) => value !== undefined && value !== null && value !== '')
        .forEach((value) => {
          const key = normalizeKey(cleanManagerName(value));
          if (key && !map.has(key)) map.set(key, label);
        });
    });
    return map;
  }, [comerciais]);

  const gestorIdMap = useMemo(() => {
    const idMap = new Map();
    const setIfAbsent = (key, label) => { if (key && !idMap.has(key)) idMap.set(key, label); };
    comerciais.forEach((item) => {
      const label = cleanManagerName(item.label || item.display_name || item.username || item.value || item.name || item.displayName || '');
      if (!label) return;
      if (item.id !== undefined && item.id !== null) setIfAbsent(String(item.id), label);
      if (item.value !== undefined && item.value !== null) setIfAbsent(String(item.value), label);
      if (item.username) setIfAbsent(String(item.username), label);
    });
    return idMap;
  }, [comerciais]);

  const getGestor = useCallback((ficha) => {
    const candidates = [ficha.comercial_id, ficha.author, ficha.autor, ficha.gestor, ficha.nome_autor, ficha.nomeAutor, ficha.user];
    for (const raw of candidates) {
      if (raw === undefined || raw === null || raw === '') continue;
      const rawStr = String(raw).trim();
      // try id-based mapping first
      const byId = gestorIdMap.get(rawStr);
      if (byId) return byId;
      // try normalized name mapping
      const cleaned = cleanManagerName(rawStr);
      const mapped = gestorMap.get(normalizeKey(cleaned));
      if (mapped) return mapped;
      // fallback to cleaned name
      if (cleaned && cleaned !== '—') return cleaned;
    }
    return '—';
  }, [gestorMap, gestorIdMap]);

  const normalizedRole = normalizeRole(user?.role);
  // "editor" fica restrito ao seu próprio conteúdo nos relatórios de
  // Contactos, Gestores, Propostas e Propostas Adjudicadas; "contributor" e
  // "admin" veem sempre os dados de todos.
  const isCurrentUserPrivileged = normalizedRole === 'contributor' || normalizedRole === 'admin';
  const allGestorNames = useMemo(() => {
    const fromComerciais = comerciais
      .map((item) => cleanManagerName(item.label || item.display_name || item.username || item.value || ''))
      .filter((name) => name && name !== '—');

    const fromFichas = fichas
      .map((ficha) => getGestor(ficha))
      .filter((name) => name && name !== '—');

    return [...new Set([...fromComerciais, ...fromFichas])].sort((a, b) => a.localeCompare(b, 'pt'));
  }, [comerciais, fichas, getGestor]);

  const currentUserIdentityKeys = useMemo(() => {
    const keys = new Set();
    [
      user?.username,
      user?.email,
      user?.name,
      user?.displayName,
      `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
    ]
      .filter((value) => value !== undefined && value !== null && value !== '')
      .forEach((value) => {
        const raw = (value || '').toString().trim();
        if (!raw) return;
        keys.add(normalizeKey(raw));
        keys.add(normalizeKey(cleanManagerName(raw)));
      });
    return keys;
  }, [user]);

  const visibleFichas = (() => {
    if (!user || isCurrentUserPrivileged) return fichas;

    return fichas.filter((ficha) => {
      const candidates = [
        ficha.author,
        ficha.autor,
        ficha.gestor,
        ficha.comercial_id,
        ficha.nome_autor,
        getGestor(ficha)
      ];

      return candidates.some((value) => {
        const raw = (value || '').toString().trim();
        if (!raw) return false;
        return currentUserIdentityKeys.has(normalizeKey(raw)) || currentUserIdentityKeys.has(normalizeKey(cleanManagerName(raw)));
      });
    });
  })();

  const getFichaId = (ficha) => ficha.id || ficha.legacy_id || ficha.ID;
  const getFichaTitulo = (ficha) => asText(ficha.title, ficha.titulo, ficha.post_title, ficha.tipo_contacto, ficha.nome) || '(sem título)';

  // propostasMeta is keyed by legacy_id (= WordPress post_id, where the real
  // serviços/valores/faturação live in wp_postmeta — see GET
  // /api/fichas/propostas-meta on the backend). Not getFichaId(ficha), which
  // prefers the local `id` — legacy_id is the only key that matches.
  const getPropostaMeta = (ficha) => propostasMeta[ficha.legacy_id]?.proposta || null;
  const getAdjudicadaMeta = (ficha) => propostasMeta[ficha.legacy_id]?.adjudicada || null;

  // "Escolha uma opção" is the Pods dropdown's own placeholder value — filled
  // on thousands of fichas that never actually had a real estado chosen, so
  // it's treated as empty rather than a real state.
  const isEstadoPlaceholder = (value) => {
    const raw = (value || '').toString().trim().toLowerCase();
    return !raw || raw === 'escolha uma opção' || raw === 'escolha uma opcao';
  };

  const hasPropostaReal = (ficha) => {
    const meta = getPropostaMeta(ficha);
    if (!meta) return false;
    return (meta.servicos && meta.servicos.length > 0) || parseMoeda(meta.valorTotal) !== null || !!cleanReportText(meta.descritivo);
  };

  const isAdjudicadaReal = (ficha) => {
    const meta = getAdjudicadaMeta(ficha);
    if (!meta) return false;
    return (meta.servicos && meta.servicos.length > 0)
      || parseMoeda(meta.valorTotal) !== null
      || !!cleanReportText(meta.descritivoFatura)
      || parseMoeda(meta.valorFatura) !== null;
  };
  // "Contactos a Efetuar" só existe para fichas com data do PRÓXIMO contacto
  // preenchida — sem fallback para data_contacto/created_at/updated_at, que
  // são datas de contactos já passados, não do que falta fazer.
  const getContactoData = (ficha) => ficha.data_proximo_contacto;
  const getGestorClienteData = (item) => item.dataContacto;
  const getSummaryFlag = (ficha, names) => names.some((name) => toBool(ficha[name]));
  const getDuracaoLabel = (ficha) => {
    const direct = asText(ficha.duracao_contacto, ficha.duracao);
    if (direct) return direct;
    // inicio_contacto/fim_contacto são os nomes reais resolvidos pelo backend
    // (ver GET /api/fichas) — hora_inicio_contacto/hora_inicio nunca existiram.
    const ini = asText(ficha.inicio_contacto, ficha.hora_inicio_contacto, ficha.hora_inicio);
    const fim = asText(ficha.fim_contacto, ficha.hora_fim_contacto, ficha.hora_fim);
    if (ini || fim) return `${ini || '--:--'} - ${fim || '--:--'}`;
    return '—';
  };

  const withDerivedSignals = (item) => {
    const motivo = `${item.motivoResumo || ''} ${item.motivoTipoProximo || ''}`;

    return {
      ...item,
      // contacto_efetuado, follow_up e novo_contacto agora vêm de campos reais
      // e fiáveis (ver GET /api/fichas no backend) — os fallbacks anteriores
      // (ex: "|| !!item.dataContacto", que tornava sinalRealizado quase sempre
      // verdadeiro, ou heurísticas por palavra-chave) eram uma compensação
      // para dados que estavam sempre vazios e já não fazem sentido: com dados
      // reais, esses fallbacks só introduziam falsos positivos.
      sinalRealizado: item.contactoEfetuado,
      sinalFollowUp: item.followUp,
      sinalNovo: item.novoContacto,
      // "Reforço pedido" e "Contacto desmarcado" continuam sem campo próprio
      // nos dados (não fazem parte dos campos validados nesta correção), por
      // isso mantêm-se como heurística por palavra-chave.
      sinalReforco: item.reforcoPedido || hasKeyword(motivo, /reforc|reforço|insist|voltar\s+a\s+ligar/),
      sinalDesmarcado: item.contactoDesmarcado || hasKeyword(motivo, /desmarcad|nao\s+atendeu|não\s+atendeu|sem\s+resposta|nao\s+atende/),
      // "Agendado" passa a depender só de haver mesmo uma data de próximo
      // contacto marcada — antes também contava só ter um "tipo" de próximo
      // contacto escolhido sem data nenhuma, o que passou a inflacionar esta
      // métrica assim que esse campo ficou fiável.
      sinalAgendado: !!item.dataProximoContacto
    };
  };
  const clienteOptions = (() => {
    const fromFichas = visibleFichas
      .map((ficha) => getClienteNome(ficha))
      .filter((name) => name && name !== '—');

    const fromClientes = clientes
      .map((cliente) => (cliente.denominacao_fiscal || cliente.nome || cliente.client_name || cliente.cliente_nome || cliente.nome_cliente || '').toString().trim())
      .filter(Boolean);

    const names = [...new Set([...fromFichas, ...fromClientes])].sort((a, b) => a.localeCompare(b, 'pt'));
    return ['Todos', ...names];
  })();

  const gestorOptions = (() => {
    const source = allGestorNames.length
      ? allGestorNames
      : [...new Set(visibleFichas.map((ficha) => getGestor(ficha)).filter((name) => name && name !== '—'))]
        .sort((a, b) => a.localeCompare(b, 'pt'));

    return source.length ? ['Todos', ...source] : ['Todos'];
  })();

  // Lista fixa (Telefónico/Email/Reunião/Visita), tal como já usada nos
  // resumos do relatório — garante que o filtro cobre sempre estas 4
  // opções mesmo que, num dado momento, faltem dados com algum destes tipos.
  const tipoContactoOptions = ['Todos', ...TIPOS_CONTACTO];

  const localidadeOptions = (() => {
    const names = [...new Set(
      clientes.map((cliente) => asText(cliente.localidade)).filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, 'pt'));
    return ['Todos', ...names];
  })();

  useEffect(() => {
    if (isCurrentUserPrivileged) return;
    if (!gestorOptions.length) return;
    const fallbackGestor = gestorOptions[0];
    if (!gestorOptions.includes(gestor)) setGestor(fallbackGestor);
    if (!gestorOptions.includes(submittedGC.gestor)) {
      setSubmittedGC((prev) => ({ ...prev, gestor: fallbackGestor }));
    }
  }, [isCurrentUserPrivileged, gestorOptions, gestor, submittedGC.gestor]);

  // Sem filtro de data escolhido, a aba deve mostrar logo todos os contactos
  // com data do próximo contacto preenchida — não deve depender de um clique
  // em "Ver contactos a efetuar" para aparecer algo.
  useEffect(() => {
    if (tipo === 'contactos-a-efetuar') {
      setShowContactos(true);
    }
  }, [tipo]);

  // Aba "Propostas" (Contacto Comercial): só fichas com um valor real
  // preenchido na parte de serviços propostos (ver hasPropostaReal acima),
  // agrupadas por mês/ano de apresentação da proposta.
  const propostasReaisFiltradas = (() => {
    // "Propostas" e "Propostas Adjudicadas" são mutuamente exclusivas — uma
    // ficha com proposta que já foi adjudicada passa a contar só na aba de
    // adjudicadas, para não aparecer nas duas em simultâneo.
    const base = visibleFichas.filter((ficha) => hasPropostaReal(ficha) && !isAdjudicadaReal(ficha));
    const byCliente = submittedClienteProp === 'Todos'
      ? base
      : base.filter((ficha) => getClienteNome(ficha) === submittedClienteProp);

    const pesquisa = pesquisaPropostas.trim().toLowerCase();
    if (!pesquisa) return byCliente;

    // Só por serviço/descritivo — o cliente já tem o seu próprio filtro
    // dedicado acima, não faz sentido pesquisar por ele aqui também.
    return byCliente.filter((ficha) => {
      const meta = getPropostaMeta(ficha);
      const composed = [
        meta?.descritivo,
        ...(meta?.servicos || []).map((s) => s.servico)
      ].filter(Boolean).join(' ').toLowerCase();
      return composed.includes(pesquisa);
    });
  })();

  const propostasAgrupadasPorMes = useMemo(
    () => groupItemsByMonth(propostasReaisFiltradas, (ficha) => getPropostaMeta(ficha)?.dataApresentacao),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getPropostaMeta is a plain closure over propostasMeta, already listed
    [propostasReaisFiltradas, propostasMeta]
  );

  // Aba "Propostas Adjudicadas" (Contacto Financeiro): só fichas com um
  // valor real preenchido em serviços adjudicados/faturação (ver
  // isAdjudicadaReal acima), agrupadas por mês/ano — mesma lógica de
  // agrupamento temporal usada em "Propostas" e "Contactos a Efetuar".
  const adjudicadasReaisFiltradas = (() => {
    const base = visibleFichas.filter((ficha) => isAdjudicadaReal(ficha));
    const byCliente = submittedClienteAdj === 'Todos'
      ? base
      : base.filter((ficha) => getClienteNome(ficha) === submittedClienteAdj);

    const pesquisa = pesquisaAdjudicadas.trim().toLowerCase();
    if (!pesquisa) return byCliente;

    // Só por serviço/descritivo — o cliente já tem o seu próprio filtro
    // dedicado acima, não faz sentido pesquisar por ele aqui também.
    return byCliente.filter((ficha) => {
      const meta = getAdjudicadaMeta(ficha);
      const composed = [
        meta?.descritivoFatura,
        ...(meta?.servicos || []).map((s) => s.servico)
      ].filter(Boolean).join(' ').toLowerCase();
      return composed.includes(pesquisa);
    });
  })();

  // Sem uma "data de adjudicação" própria nos dados, a data da fatura é o
  // sinal temporal mais próximo disso; sem fatura ainda emitida, cai de
  // volta na data de apresentação da proposta original.
  const getAdjudicadaDataGrupo = (ficha) => getAdjudicadaMeta(ficha)?.dataFatura || getPropostaMeta(ficha)?.dataApresentacao;

  const adjudicadasAgrupadasPorMes = useMemo(
    () => groupItemsByMonth(adjudicadasReaisFiltradas, getAdjudicadaDataGrupo),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getAdjudicadaDataGrupo is a plain closure over propostasMeta, already listed
    [adjudicadasReaisFiltradas, propostasMeta]
  );

  const totalConsolidadoAdjudicadas = useMemo(
    () => adjudicadasReaisFiltradas.reduce((sum, ficha) => sum + (parseMoeda(getAdjudicadaMeta(ficha)?.valorTotal) || 0), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getAdjudicadaMeta is a plain closure over propostasMeta, already listed
    [adjudicadasReaisFiltradas, propostasMeta]
  );

  const contactosFiltrados = (() => {
    const contactosBase = visibleFichas.filter((ficha) => !!toDateOnly(ficha.data_proximo_contacto));

    const filtrados = submittedDataContactos
      ? contactosBase.filter((ficha) => toDateOnly(ficha.data_proximo_contacto) === submittedDataContactos)
      : contactosBase;

    return filtrados
      .sort((a, b) => {
        const da = toDateOnly(getContactoData(a));
        const db = toDateOnly(getContactoData(b));
        const byDate = da.localeCompare(db);
        if (byDate !== 0) return byDate;

        const ca = String(getClienteNome(a)).toLowerCase();
        const cb = String(getClienteNome(b)).toLowerCase();
        return ca.localeCompare(cb, 'pt');
      });
  })();

  const contactosAgrupadosPorMes = useMemo(
    () => groupItemsByMonth(contactosFiltrados, getContactoData),
    [contactosFiltrados]
  );

  // Debug temporário: se esta aba continuar vazia mesmo depois de clicar em
  // "Ver contactos a efetuar" sem data escolhida, abrir a consola do browser
  // aqui mostra se o bloqueio é no filtro (submittedDataContactos com valor
  // inesperado) ou nos próprios dados (nenhuma ficha visível tem
  // data_proximo_contacto preenchida, o que já não é um bug de UI).
  useEffect(() => {
    if (tipo !== 'contactos-a-efetuar') return;
    console.debug('[Contactos a Efetuar] estado do filtro:', {
      dataContactos,
      submittedDataContactos,
      totalFichasVisiveis: visibleFichas.length,
      totalComDataProximoContacto: visibleFichas.filter((f) => !!(f.data_proximo_contacto || '').toString().trim()).length,
      contactosFiltrados: contactosFiltrados.length
    });
  }, [tipo, dataContactos, submittedDataContactos, visibleFichas, contactosFiltrados]);

  // BUG CRÍTICO CORRIGIDO (histórico): faltava o "useMemo(" — isto era
  // `(() => {...}, [deps])`, que o JS interpreta como um *operador vírgula*
  // dentro de parêntesis: cria a função (nunca a chama), depois avalia
  // [deps] e usa isso como valor final — ou seja, esta variável apontava
  // sempre para o array de dependências (2 elementos errados) em vez da
  // lista de fichas processada. Todos os `.filter(item => item.sinalX)` a
  // jusante corriam sobre objetos sem essas propriedades e devolviam sempre
  // [], daí os relatórios de Gestores aparecerem persistentemente a
  // zeros/vazios.
  const gestorClientesFiltrados = useMemo(() => {
    const base = visibleFichas.map((ficha) => {
      const dataContactoStr = toDateOnly(ficha.data_contacto || ficha.post_date || ficha.created_at || ficha.updated_at);
      return withDerivedSignals({
        fichaId: getFichaId(ficha),
        fichaTitulo: getFichaTitulo(ficha),
        gestorNome: getGestor(ficha),
        clienteNome: getClienteNome(ficha),
        clienteId: getClienteId(ficha),
        tipoCliente: getTipoCliente(ficha, dataContactoStr),
        localidade: getClienteLocalidade(ficha),
        dataContacto: dataContactoStr,
        dataProximoContacto: toDateOnly(ficha.data_proximo_contacto),
        duracao: getDuracaoLabel(ficha),
        tipoContacto: asText(ficha.tipo_contacto, ficha.tipo_contato) || '—',
        contactoEfetuado: getSummaryFlag(ficha, ['contacto_efetuado', 'contactoEfetuado', 'contacto_efetuado_flag']),
        motivoResumo: asText(ficha.motivo_resumo_contacto, ficha.motivo_resumo, ficha.post_content) || '—',
        motivoTipoProximo: asText(ficha.motivo_tipo_proximo_contacto, ficha.tipo_proximo_contacto, ficha.motivo_proximo_contacto) || '—',
        assuntoTratado: getSummaryFlag(ficha, ['assunto_tratado', 'assuntoTratado', 'assunto_tratado_flag']),
        followUp: getSummaryFlag(ficha, ['follow_up', 'followUp']),
        reforcoPedido: getSummaryFlag(ficha, ['reforcos_pedidos_contacto', 'reforco_pedido_contacto', 'reforco_pedido']),
        novoContacto: getSummaryFlag(ficha, ['novo_contacto', 'novoContacto']),
        contactoDesmarcado: getSummaryFlag(ficha, ['contacto_desmarcado', 'contactoDesmarcado'])
      });
    });

    // Filtro 100% flexível: cada campo (gestor, cliente, data início, data
    // fim) é aplicado só se estiver preenchido no momento em que se carrega
    // em "Filtrar" — nenhum depende dos outros para funcionar.
    const {
      gestor: subGestor, cliente: subCliente, tipoContacto: subTipoContacto,
      localidade: subLocalidade, dataInicio: subDataInicio, dataFim: subDataFim
    } = submittedGC;

    const filtered = base.filter(item => {
      const normalizedItemGestor = normalizeKey(cleanManagerName(item.gestorNome));
      const normalizedSubGestor = normalizeKey(cleanManagerName(subGestor));
      const byGestor = subGestor === 'Todos' ? true : normalizedItemGestor === normalizedSubGestor;

      const normalizedItemCliente = normalizeKey(item.clienteNome || '');
      const normalizedSubCliente = normalizeKey(subCliente || '');
      const byCliente = subCliente === 'Todos' ? true : normalizedItemCliente === normalizedSubCliente;

      const byTipoContacto = (!subTipoContacto || subTipoContacto === 'Todos')
        ? true
        : normalizeKey(item.tipoContacto || '') === normalizeKey(subTipoContacto);

      const byLocalidade = (!subLocalidade || subLocalidade === 'Todos')
        ? true
        : normalizeKey(item.localidade || '') === normalizeKey(subLocalidade);

      const byDataInicio = !subDataInicio ? true : (item.dataContacto && item.dataContacto >= subDataInicio);
      const byDataFim = !subDataFim ? true : (item.dataContacto && item.dataContacto <= subDataFim);

      return byGestor && byCliente && byTipoContacto && byLocalidade && byDataInicio && byDataFim;
    });

    return filtered.sort((a, b) => {
      const g = a.gestorNome.localeCompare(b.gestorNome, 'pt');
      if (g !== 0) return g;
      const d = (b.dataContacto || '').localeCompare(a.dataContacto || '');
      if (d !== 0) return d;
      return a.fichaTitulo.localeCompare(b.fichaTitulo, 'pt');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getFichaId/getGestor/getClienteNome/getClienteId/getDuracaoLabel/withDerivedSignals/getTipoCliente are plain closures recreated every render; visibleFichas and submittedGC are the only inputs that actually change what this computes
  }, [visibleFichas, submittedGC]);

  const gestorClientesAgrupadosPorGestor = useMemo(() => {
    const groups = new Map();

    gestorClientesFiltrados.forEach((item) => {
      const gestorNome = (cleanManagerName(item.gestorNome) === '—' ? '' : cleanManagerName(item.gestorNome)) || 'Sem gestor';
      if (!groups.has(gestorNome)) groups.set(gestorNome, []);
      groups.get(gestorNome).push(item);
    });

    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b, 'pt'))
      .map(([gestorNome, items]) => ({
        gestorNome,
        resumo: buildResumoMetrics(items),
        datas: groupItemsByDate(items, getGestorClienteData)
      }));
  }, [gestorClientesFiltrados]);

  const resumoGlobalGestorClientes = useMemo(
    () => buildResumoMetrics(gestorClientesFiltrados),
    [gestorClientesFiltrados]
  );

  // "Ficha Detalhada do Cliente" — mesma estrutura do modelo clássico em
  // papel: cabeçalho do cliente, histórico de contactos (fichas ligadas a
  // este cliente) e orçamentos agrupados por estado comercial.
  const clienteFichaSelecionada = useMemo(() => {
    if (submittedClienteFicha === 'Todos') return null;

    const clienteInfo = clientes.find((c) => {
      const nome = (c.denominacao_fiscal || c.nome || c.client_name || '').toString().trim();
      return nome === submittedClienteFicha;
    }) || null;

    const fichasDoCliente = visibleFichas.filter((ficha) => getClienteNome(ficha) === submittedClienteFicha);

    const historico = fichasDoCliente
      .map((ficha) => ({
        fichaId: getFichaId(ficha),
        data: toDateOnly(ficha.data_contacto || ficha.post_date || ficha.created_at || ficha.updated_at),
        gestorNome: getGestor(ficha),
        tipoContacto: asText(ficha.tipo_contacto) || TIPO_CONTACTO_OUTRO,
        novoContacto: toBool(ficha.novo_contacto),
        contactoEfetuado: toBool(ficha.contacto_efetuado),
        followUp: toBool(ficha.follow_up),
        motivoResumo: asText(ficha.motivo_resumo_contacto) || '—'
      }))
      .filter((item) => item.data)
      .sort((a, b) => a.data.localeCompare(b.data));

    const orcamentos = { 'Em Análise': [], Aprovados: [], Rejeitados: [] };
    fichasDoCliente.forEach((ficha) => {
      const meta = getPropostaMeta(ficha);
      const bucket = meta && !isEstadoPlaceholder(meta.estado) ? ESTADO_PROPOSTA_BUCKET[meta.estado] : null;
      if (!bucket) return;
      orcamentos[bucket].push({
        numero: getFichaId(ficha),
        descricao: cleanReportText(stripHtml(meta.descritivo)) || (meta.servicos || []).map((s) => s.servico).filter(Boolean).join(', ') || '—',
        valor: parseMoeda(meta.valorTotal)
      });
    });
    ORCAMENTO_BUCKETS.forEach((bucket) => orcamentos[bucket].sort((a, b) => a.numero - b.numero));

    const valorAprovados = orcamentos.Aprovados.reduce((sum, o) => sum + (o.valor || 0), 0);
    const valorTotalOrcamentos = ORCAMENTO_BUCKETS.reduce(
      (sum, bucket) => sum + orcamentos[bucket].reduce((s, o) => s + (o.valor || 0), 0),
      0
    );

    return {
      nome: submittedClienteFicha,
      gestorNome: clienteInfo ? (clienteInfo.autor_nome || getGestor(clienteInfo)) : (historico[0]?.gestorNome || '—'),
      morada: asText(clienteInfo?.morada) || '—',
      nif: asText(clienteInfo?.nif) || '—',
      pessoaContacto: asText(clienteInfo?.pessoa_contacto_nome) || '—',
      cargo: asText(clienteInfo?.pessoa_contacto_cargo) || '—',
      telefone: asText(clienteInfo?.contacto_empresa) || '—',
      contactoPessoa: asText(clienteInfo?.pessoa_contacto_telefone_email) || '—',
      historico,
      orcamentos,
      totais: {
        contactos: historico.length,
        orcamentos: ORCAMENTO_BUCKETS.reduce((sum, bucket) => sum + orcamentos[bucket].length, 0),
        valorAprovados,
        valorTotalOrcamentos
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getClienteNome/getFichaId/getGestor/getPropostaMeta/isEstadoPlaceholder are plain closures over clientes/propostasMeta, already covered by these deps
  }, [submittedClienteFicha, clientes, visibleFichas, propostasMeta]);

  const exportGestorClientesToExcel = async () => {
    if (gestorClientesFiltrados.length === 0) return;
    const workbook = new ExcelJS.Workbook();

    const usedSheetNames = new Set();
    const getUniqueSheetName = (baseName) => {
      const cleaned = String(baseName || 'Sem gestor')
        .replace(/[\\/?*:]/g, ' ')
        .replace(/\[/g, ' ')
        .replace(/\]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim() || 'Sem gestor';
      const maxLen = 31;
      let candidate = cleaned.slice(0, maxLen);
      let n = 2;

      while (usedSheetNames.has(candidate)) {
        const suffix = ` (${n})`;
        candidate = `${cleaned.slice(0, Math.max(1, maxLen - suffix.length))}${suffix}`;
        n += 1;
      }

      usedSheetNames.add(candidate);
      return candidate;
    };

    const addTitleRow = (sheet, text) => {
      const row = sheet.addRow([text]);
      row.font = { bold: true, size: 14 };
    };
    const addSectionRow = (sheet, text) => {
      const row = sheet.addRow([text]);
      row.font = { bold: true };
      row.eachCell({ includeEmpty: true }, (cell) => { cell.fill = XLSX_SECTION_FILL; });
    };
    const addMetricRow = (sheet, label, value) => {
      const row = sheet.addRow([label, value]);
      row.getCell(1).font = { bold: false };
      row.getCell(2).font = { bold: true };
    };
    const addBreakdownRows = (sheet, title, breakdown) => {
      addSectionRow(sheet, title);
      Object.entries(breakdown).forEach(([label, count]) => {
        if (count > 0) addMetricRow(sheet, label, count);
      });
    };
    const addResumoBlock = (sheet, resumo) => {
      addSectionRow(sheet, 'Resumo');
      addMetricRow(sheet, 'Total de Contactos', resumo.total);
      addMetricRow(sheet, 'Nº de Contactos Realizados', resumo.realizados);
      addMetricRow(sheet, 'Nº de Contactos Agendados', resumo.agendados);
      addMetricRow(sheet, 'Nº de Follow Up', resumo.followUp);
      addMetricRow(sheet, 'Nº de Reforços de Pedidos de Contacto', resumo.reforcos);
      addMetricRow(sheet, 'Nº de Novos Contactos', resumo.novos);
      addMetricRow(sheet, 'Nº de Contactos Desmarcados', resumo.desmarcados);
      sheet.addRow([]);
      addBreakdownRows(sheet, 'Por Tipo de Contacto', resumo.porTipoContacto);
      sheet.addRow([]);
      addBreakdownRows(sheet, 'Por Tipo de Cliente', resumo.porTipoCliente);
      sheet.addRow([]);
      addBreakdownRows(sheet, 'Por Localidade', resumo.porLocalidade);
      sheet.addRow([]);
    };

    // --- Resumo Geral ---
    const resumoSheet = workbook.addWorksheet(getUniqueSheetName('Resumo Geral'));
    resumoSheet.columns = [{ width: 46 }, { width: 16 }];
    addTitleRow(resumoSheet, 'Gestores');
    resumoSheet.addRow([]);
    addResumoBlock(resumoSheet, resumoGlobalGestorClientes);
    applyBordersToSheet(resumoSheet);

    // --- Uma folha por gestor, com resumo próprio + tabela de detalhe ---
    const DETAIL_HEADERS = [
      'Data', 'Ficha', 'Cliente', 'Tipo de Cliente', 'Localidade', 'Duração', 'Tipo de contacto',
      'Contacto efetuado?', 'Motivo/Resumo do Contacto', 'Motivo/Tipo do Próximo Contacto',
      'Data do Próximo Contacto', 'Processo Encerrado?'
    ];
    // Coluna A é partilhada, nesta mesma folha, entre a tabela de detalhe
    // (datas curtas) e os rótulos do resumo do gestor logo acima (ex: "Nº de
    // Reforços de Pedidos de Contacto") — tem de ser larga o suficiente para
    // esses rótulos não ficarem cortados, não apenas para as datas.
    const DETAIL_WIDTHS = [42, 42, 32, 14, 16, 12, 16, 16, 46, 34, 18, 14];

    gestorClientesAgrupadosPorGestor.forEach((gestorGrupo) => {
      const sheet = workbook.addWorksheet(getUniqueSheetName(gestorGrupo.gestorNome));
      sheet.columns = DETAIL_WIDTHS.map((width) => ({ width }));

      addTitleRow(sheet, `Gestor: ${gestorGrupo.gestorNome}`);
      sheet.addRow([]);
      addResumoBlock(sheet, gestorGrupo.resumo);

      const headerRow = sheet.addRow(DETAIL_HEADERS);
      headerRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = XLSX_HEADER_FILL;
        cell.font = XLSX_HEADER_FONT;
        cell.border = { bottom: XLSX_THIN_BORDER };
      });

      let rowIndex = 0;
      gestorGrupo.datas.forEach((grupo) => {
        grupo.items.forEach((item) => {
          rowIndex += 1;
          const row = sheet.addRow([
            grupo.dateLabel,
            item.fichaTitulo,
            item.clienteNome,
            item.tipoCliente,
            item.localidade,
            item.duracao,
            item.tipoContacto,
            item.contactoEfetuado ? 'Sim' : 'Não',
            item.motivoResumo,
            item.motivoTipoProximo,
            item.dataProximoContacto ? toDatePt(item.dataProximoContacto) : 'Sem data',
            item.assuntoTratado ? 'Sim' : 'Não'
          ]);
          row.alignment = { vertical: 'top', wrapText: true };
          if (rowIndex % 2 === 0) {
            row.eachCell({ includeEmpty: true }, (cell) => { cell.fill = XLSX_STRIPE_FILL; });
          }
        });
      });
      applyBordersToSheet(sheet);
    });

    const parts = [];
    if (submittedGC.gestor !== 'Todos') parts.push(submittedGC.gestor);
    if (submittedGC.cliente !== 'Todos') parts.push(submittedGC.cliente);
    if (submittedGC.tipoContacto && submittedGC.tipoContacto !== 'Todos') parts.push(submittedGC.tipoContacto);
    if (submittedGC.localidade && submittedGC.localidade !== 'Todos') parts.push(submittedGC.localidade);
    if (submittedGC.dataInicio) parts.push(`de_${submittedGC.dataInicio}`);
    if (submittedGC.dataFim) parts.push(`ate_${submittedGC.dataFim}`);
    const suffix = parts.length ? `_${parts.join('_').replace(/[^a-z0-9_-]+/gi, '_')}` : '';

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio_gestor_cliente${suffix}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Exporta a "Ficha Detalhada" do cliente selecionado — mesma estrutura do
  // modelo clássico em papel: cabeçalho, histórico de contactos e
  // orçamentos por estado comercial, numa única folha.
  const exportClienteFichaToExcel = async () => {
    if (!clienteFichaSelecionada) return;
    const ficha = clienteFichaSelecionada;
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(ficha.nome.replace(/[\\/?*:[\]]/g, ' ').slice(0, 31) || 'Cliente');
    // Coluna A partilhada entre os rótulos do cabeçalho/resumo (ex: "Contacto
    // (Tel./Email)") e a coluna "Data"/"Nº" das tabelas mais abaixo — larga
    // o suficiente para os rótulos não ficarem cortados.
    sheet.columns = [{ width: 32 }, { width: 46 }, { width: 16 }, { width: 16 }, { width: 16 }, { width: 46 }];

    const titleRow = sheet.addRow(['Ficha Detalhada do Cliente']);
    titleRow.font = { bold: true, size: 14 };
    sheet.addRow([]);

    const sectionRow = (text) => {
      const row = sheet.addRow([text]);
      row.font = { bold: true };
      row.eachCell({ includeEmpty: true }, (cell) => { cell.fill = XLSX_SECTION_FILL; });
    };

    sectionRow(ficha.nome);
    sheet.addRow(['Gestor de Conta', ficha.gestorNome]);
    sheet.addRow(['Morada', ficha.morada]);
    sheet.addRow(['NIF', ficha.nif]);
    sheet.addRow(['Pessoa de Contacto', `${ficha.pessoaContacto}${ficha.cargo !== '—' ? ` (${ficha.cargo})` : ''}`]);
    sheet.addRow(['Telefone', ficha.telefone]);
    sheet.addRow(['Contacto (Tel./Email)', ficha.contactoPessoa]);
    sheet.addRow([]);

    sectionRow('Resumo');
    sheet.addRow(['Total de Contactos', ficha.totais.contactos]);
    sheet.addRow(['Total de Orçamentos', ficha.totais.orcamentos]);
    sheet.addRow(['Valor Aprovado', ficha.totais.valorAprovados]);
    sheet.addRow(['Valor Total (todos os estados)', ficha.totais.valorTotalOrcamentos]);
    sheet.addRow([]);

    sectionRow('Histórico de Contactos');
    const histHeaderRow = sheet.addRow(['Data', 'Descrição', 'Gestor', 'Tipo', 'Novo?', 'Efetuado?']);
    histHeaderRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = XLSX_HEADER_FILL;
      cell.font = XLSX_HEADER_FONT;
      cell.border = { bottom: XLSX_THIN_BORDER };
    });
    ficha.historico.forEach((item, idx) => {
      const row = sheet.addRow([
        toDatePt(item.data),
        item.motivoResumo,
        item.gestorNome,
        item.tipoContacto,
        item.novoContacto ? 'Sim' : 'Não',
        item.contactoEfetuado ? 'Sim' : 'Não'
      ]);
      row.alignment = { vertical: 'top', wrapText: true };
      if (idx % 2 === 1) row.eachCell({ includeEmpty: true }, (cell) => { cell.fill = XLSX_STRIPE_FILL; });
    });
    if (ficha.historico.length === 0) sheet.addRow(['Sem contactos registados.']);
    sheet.addRow([]);

    ORCAMENTO_BUCKETS.forEach((bucket) => {
      sectionRow(`Orçamentos ${bucket} (${ficha.orcamentos[bucket].length})`);
      const bucketHeaderRow = sheet.addRow(['Nº', 'Descrição', '', 'Valor']);
      bucketHeaderRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = XLSX_HEADER_FILL;
        cell.font = XLSX_HEADER_FONT;
        cell.border = { bottom: XLSX_THIN_BORDER };
      });
      ficha.orcamentos[bucket].forEach((o, idx) => {
        const row = sheet.addRow([o.numero, o.descricao, '', o.valor]);
        row.alignment = { vertical: 'top', wrapText: true };
        if (idx % 2 === 1) row.eachCell({ includeEmpty: true }, (cell) => { cell.fill = XLSX_STRIPE_FILL; });
      });
      if (ficha.orcamentos[bucket].length === 0) sheet.addRow(['Sem registos.']);
      sheet.addRow([]);
    });

    applyBordersToSheet(sheet);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ficha_cliente_${ficha.nome.replace(/[^a-z0-9_-]+/gi, '_')}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Estas duas exportações usavam a biblioteca xlsx (SheetJS Community
  // Edition), que — tal como não consegue aplicar cores (ver Gestores) —
  // também não consegue aplicar bordas às células. Passaram para ExcelJS
  // para poderem ter a borda fina preta pedida em todas as células, tal
  // como as restantes exportações.
  const downloadWorkbook = async (workbook, filename) => {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const styleHeaderRow = (row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = XLSX_HEADER_FILL;
      cell.font = XLSX_HEADER_FONT;
    });
  };

  const exportPropostasToExcel = async () => {
    if (propostasAgrupadasPorMes.length === 0) return;
    const workbook = new ExcelJS.Workbook();

    const totalValor = propostasReaisFiltradas.reduce((sum, ficha) => sum + (parseMoeda(getPropostaMeta(ficha)?.valorTotal) || 0), 0);

    const resumoSheet = workbook.addWorksheet('Resumo Geral');
    resumoSheet.columns = [{ width: 34 }, { width: 16 }, { width: 16 }];
    const titleRow = resumoSheet.addRow(['Propostas']);
    titleRow.font = { bold: true, size: 14 };
    resumoSheet.addRow([]);
    resumoSheet.addRow(['Resumo Geral']).font = { bold: true };
    resumoSheet.addRow(['Nº de propostas', propostasReaisFiltradas.length]);
    resumoSheet.addRow(['Valor total somado', totalValor]);
    resumoSheet.addRow([]);
    styleHeaderRow(resumoSheet.addRow(['Por mês', 'Nº de propostas', 'Valor total']));
    propostasAgrupadasPorMes.forEach((grupo) => {
      const subtotal = grupo.items.reduce((sum, ficha) => sum + (parseMoeda(getPropostaMeta(ficha)?.valorTotal) || 0), 0);
      resumoSheet.addRow([grupo.monthLabel, grupo.items.length, subtotal]);
    });
    applyBordersToSheet(resumoSheet);

    const detalheSheet = workbook.addWorksheet('Detalhe');
    detalheSheet.columns = [
      { width: 18 }, { width: 34 }, { width: 16 }, { width: 18 }, { width: 16 }, { width: 50 }, { width: 14 }, { width: 60 }
    ];
    styleHeaderRow(detalheSheet.addRow([
      'Mês', 'Cliente', 'Gestor', 'Data de Apresentação', 'Estado', 'Serviços Propostos', 'Valor Total', 'Descritivo'
    ]));
    propostasAgrupadasPorMes.forEach((grupo) => {
      grupo.items.forEach((ficha) => {
        const meta = getPropostaMeta(ficha) || {};
        const row = detalheSheet.addRow([
          grupo.monthLabel,
          getClienteNome(ficha),
          getGestor(ficha),
          meta.dataApresentacao ? toDatePt(meta.dataApresentacao) : 'Sem data',
          isEstadoPlaceholder(meta.estado) ? '—' : (meta.estado || '—'),
          (meta.servicos || []).map((s) => `${s.servico || '—'}: ${s.valor || '—'}`).join('; ') || '—',
          parseMoeda(meta.valorTotal) ?? '—',
          stripHtml(meta.descritivo)
        ]);
        row.alignment = { vertical: 'top', wrapText: true };
      });
    });
    applyBordersToSheet(detalheSheet);

    const suffix = submittedClienteProp !== 'Todos' ? `_${submittedClienteProp.replace(/[^a-z0-9_-]+/gi, '_')}` : '';
    await downloadWorkbook(workbook, `relatorio_propostas${suffix}.xlsx`);
  };

  const exportAdjudicadasToExcel = async () => {
    if (adjudicadasAgrupadasPorMes.length === 0) return;
    const workbook = new ExcelJS.Workbook();

    const resumoSheet = workbook.addWorksheet('Resumo Geral');
    resumoSheet.columns = [{ width: 34 }, { width: 16 }, { width: 16 }];
    const titleRow = resumoSheet.addRow(['Propostas Adjudicadas']);
    titleRow.font = { bold: true, size: 14 };
    resumoSheet.addRow([]);
    resumoSheet.addRow(['Resumo Geral']).font = { bold: true };
    resumoSheet.addRow(['Nº de propostas adjudicadas', adjudicadasReaisFiltradas.length]);
    resumoSheet.addRow(['Valor total consolidado', totalConsolidadoAdjudicadas]);
    resumoSheet.addRow([]);
    styleHeaderRow(resumoSheet.addRow(['Por mês', 'Nº de propostas', 'Valor total']));
    adjudicadasAgrupadasPorMes.forEach((grupo) => {
      const subtotal = grupo.items.reduce((sum, ficha) => sum + (parseMoeda(getAdjudicadaMeta(ficha)?.valorTotal) || 0), 0);
      resumoSheet.addRow([grupo.monthLabel, grupo.items.length, subtotal]);
    });
    applyBordersToSheet(resumoSheet);

    const detalheSheet = workbook.addWorksheet('Detalhe');
    detalheSheet.columns = [
      { width: 18 }, { width: 34 }, { width: 16 }, { width: 40 }, { width: 14 },
      { width: 40 }, { width: 14 }, { width: 14 }, { width: 18 }, { width: 20 }
    ];
    styleHeaderRow(detalheSheet.addRow([
      'Mês', 'Cliente', 'Gestor', 'Serviços Adjudicados', 'Valor Total',
      'Descritivo da Fatura', 'Valor da Fatura', 'Data da Fatura',
      'Data Prevista de Recebimento', 'Data Último Contacto Financeiro'
    ]));
    adjudicadasAgrupadasPorMes.forEach((grupo) => {
      grupo.items.forEach((ficha) => {
        const meta = getAdjudicadaMeta(ficha) || {};
        const row = detalheSheet.addRow([
          grupo.monthLabel,
          getClienteNome(ficha),
          getGestor(ficha),
          (meta.servicos || []).map((s) => `${s.servico || '—'}: ${s.valor || '—'}`).join('; ') || '—',
          parseMoeda(meta.valorTotal) ?? '—',
          stripHtml(meta.descritivoFatura),
          meta.valorFatura || '—',
          meta.dataFatura ? toDatePt(meta.dataFatura) : 'Sem data',
          meta.dataPrevistaRecebimento ? toDatePt(meta.dataPrevistaRecebimento) : 'Sem data',
          meta.dataUltimoContactoFinanceiro ? toDatePt(meta.dataUltimoContactoFinanceiro) : 'Sem data'
        ]);
        row.alignment = { vertical: 'top', wrapText: true };
      });
    });
    applyBordersToSheet(detalheSheet);

    const suffix = submittedClienteAdj !== 'Todos' ? `_${submittedClienteAdj.replace(/[^a-z0-9_-]+/gi, '_')}` : '';
    await downloadWorkbook(workbook, `relatorio_propostas_adjudicadas${suffix}.xlsx`);
  };

  const titles = {
    'propostas-adjudicadas': 'Propostas Adjudicadas',
    propostas: 'Propostas',
    'contactos-a-efetuar': 'Contactos a Efetuar',
    gestores: 'Gestores',
    clientes: 'Clientes'
  };

  return (
    <div style={pageStyle}>
      <h2 style={{ fontWeight: 400, fontSize: '1.8rem', marginBottom: 20 }}>
        {titles[tipo] || 'Relatórios'}
      </h2>

      {loading && <p style={{ marginTop: 6 }}>A carregar dados...</p>}

      {tipo === 'propostas-adjudicadas' && (
        <>
          <div style={rowStyle}>
            <label style={labelStyle}>Cliente</label>
            <select value={clienteAdj} onChange={(e) => setClienteAdj(e.target.value)} style={selectStyle}>
              {clienteOptions.map((c) => <option key={c}>{c}</option>)}
            </select>
            <button
              type="button"
              style={actionBtn}
              onClick={() => {
                setSubmittedClienteAdj(clienteAdj);
                setExpandedMesesAdjudicadas({});
              }}
            >
              Filtrar
            </button>
            <input
              type="search"
              placeholder="Pesquisar por serviço..."
              value={pesquisaAdjudicadas}
              onChange={(e) => setPesquisaAdjudicadas(e.target.value)}
              style={searchInputStyle}
            />
            <button
              type="button"
              style={exportBtn}
              onClick={exportAdjudicadasToExcel}
              disabled={adjudicadasReaisFiltradas.length === 0}
            >
              Exportar para Excel
            </button>
          </div>

          <PropostasAdjudicadasFinanceiro
            grupos={adjudicadasAgrupadasPorMes}
            totalCount={adjudicadasReaisFiltradas.length}
            expandedMeses={expandedMesesAdjudicadas}
            setExpandedMeses={setExpandedMesesAdjudicadas}
            onOpenFicha={(id) => navigate(`/fichas/${id}/editar`)}
            getFichaId={getFichaId}
            getClienteNome={getClienteNome}
            getGestor={getGestor}
            getAdjudicadaMeta={getAdjudicadaMeta}
          />
        </>
      )}

      {tipo === 'propostas' && (
        <>
          <div style={rowStyle}>
            <label style={labelStyle}>Cliente</label>
            <select value={clienteProp} onChange={(e) => setClienteProp(e.target.value)} style={selectStyle}>
              {clienteOptions.map((c) => <option key={c}>{c}</option>)}
            </select>
            <button
              type="button"
              style={actionBtn}
              onClick={() => {
                setSubmittedClienteProp(clienteProp);
                setExpandedMesesPropostas({});
              }}
            >
              Filtrar
            </button>
            <input
              type="search"
              placeholder="Pesquisar por serviço..."
              value={pesquisaPropostas}
              onChange={(e) => setPesquisaPropostas(e.target.value)}
              style={searchInputStyle}
            />
            <button
              type="button"
              style={exportBtn}
              onClick={exportPropostasToExcel}
              disabled={propostasReaisFiltradas.length === 0}
            >
              Exportar para Excel
            </button>
          </div>

          <PropostasComerciais
            grupos={propostasAgrupadasPorMes}
            totalCount={propostasReaisFiltradas.length}
            expandedMeses={expandedMesesPropostas}
            setExpandedMeses={setExpandedMesesPropostas}
            onOpenFicha={(id) => navigate(`/fichas/${id}/editar`)}
            getFichaId={getFichaId}
            getClienteNome={getClienteNome}
            getGestor={getGestor}
            getPropostaMeta={getPropostaMeta}
            isEstadoPlaceholder={isEstadoPlaceholder}
          />
        </>
      )}

      {tipo === 'contactos-a-efetuar' && (
        <>
          <div style={rowStyle}>
            <label style={labelStyle}>Data</label>
            <input type="date" value={dataContactos} onChange={(e) => setDataContactos(e.target.value)} style={dateInputStyle} />
            <button
              type="button"
              style={actionBtn}
              onClick={() => {
                // dataContactos já é sempre string ('' quando o campo está
                // vazio), mas normaliza de forma explícita para nunca
                // propagar null/undefined para o filtro — vazio == mostrar
                // todos os contactos com data do próximo contacto preenchida.
                setSubmittedDataContactos(dataContactos || '');
                setShowContactos(true);
                setExpandedDatasContactos({});
              }}
            >
              Ver contactos a efetuar
            </button>
          </div>

          {showContactos && (
            <div style={{ marginTop: 18 }}>
              <h3 style={reportHeading}>
                {submittedDataContactos ? `Clientes a contactar em: ${toDatePt(submittedDataContactos)}` : 'Clientes a contactar'}
              </h3>
              {contactosFiltrados.length === 0 ? (
                <p style={{ marginTop: 8 }}>{submittedDataContactos ? 'Sem contactos para esta data.' : 'Sem contactos a efetuar.'}</p>
              ) : (
                <div style={listStyle}>
                  {contactosAgrupadosPorMes.map((grupo) => (
                    <div key={`mes-${grupo.monthKey}`} style={dateSectionStyle}>
                      <button
                        type="button"
                        style={dateToggleSimpleStyle}
                        onClick={() => {
                          setExpandedDatasContactos((prev) => ({
                            ...prev,
                            [grupo.monthKey]: !(prev[grupo.monthKey] ?? true)
                          }));
                        }}
                      >
                        <span style={dateChevronSimpleStyle}>{(expandedDatasContactos[grupo.monthKey] ?? true) ? '▼' : '▶'}</span>
                        <span style={dateHeadingStyle}>{grupo.monthLabel}</span>
                        <span style={dateCountSimpleStyle}>({grupo.items.length})</span>
                      </button>

                      {(expandedDatasContactos[grupo.monthKey] ?? true) && (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={servicosTableStyle}>
                            <thead>
                              <tr>
                                <th style={servicosThStyle}>Data</th>
                                <th style={servicosThStyle}>Gestor</th>
                                <th style={servicosThStyle}>Cliente</th>
                                <th style={servicosThStyle}>Tipo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {grupo.items.map((ficha) => {
                                const clienteId = getClienteId(ficha);
                                const clienteNome = getClienteNome(ficha);
                                return (
                                  <tr key={`contacto-${getFichaId(ficha)}`}>
                                    <td style={servicosTdStyle}>{toDatePt(ficha.data_proximo_contacto)}</td>
                                    <td style={servicosTdStyle}>{getGestor(ficha)}</td>
                                    <td style={servicosTdStyle}>
                                      <button
                                        type="button"
                                        style={reportLinkStyle}
                                        onClick={() => {
                                          if (clienteId) navigate(`/clientes/${clienteId}/editar`);
                                        }}
                                        disabled={!clienteId}
                                        title={clienteId ? 'Editar cliente' : 'Cliente sem ligação'}
                                      >
                                        {clienteNome}
                                      </button>
                                    </td>
                                    <td style={servicosTdStyle}>{asText(ficha.tipo_proximo_contacto) || '—'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tipo === 'gestores' && (
        <>
          <div style={gcGridStyle}>
            <div style={gcFieldStyle}>
              <label style={gcLabelStyle}>Gestor</label>
              <select value={gestor} onChange={(e) => setGestor(e.target.value)} style={gcControlStyle}>
                {gestorOptions.map((g) => <option key={g}>{g}</option>)}
              </select>
            </div>
            <div style={gcFieldStyle}>
              <label style={gcLabelStyle}>Cliente</label>
              <select value={clienteGC} onChange={(e) => setClienteGC(e.target.value)} style={gcControlStyle}>
                {clienteOptions.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div style={gcFieldStyle}>
              <label style={gcLabelStyle}>Tipo de Contacto</label>
              <select value={tipoContactoGC} onChange={(e) => setTipoContactoGC(e.target.value)} style={gcControlStyle}>
                {tipoContactoOptions.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div style={gcFieldStyle}>
              <label style={gcLabelStyle}>Localidade</label>
              <select value={localidadeGC} onChange={(e) => setLocalidadeGC(e.target.value)} style={gcControlStyle}>
                {localidadeOptions.map((l) => <option key={l}>{l}</option>)}
              </select>
            </div>
            <div style={gcFieldStyle}>
              <label style={{ ...gcLabelStyle, color: '#c0392b' }}>Data de Início</label>
              <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} style={gcControlStyle} />
            </div>
            <div style={gcFieldStyle}>
              <label style={{ ...gcLabelStyle, color: '#c0392b' }}>Data de Fim</label>
              <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} style={gcControlStyle} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              type="button"
              style={actionBtn}
              onClick={() => {
                setSubmittedGC({ gestor, cliente: clienteGC, tipoContacto: tipoContactoGC, localidade: localidadeGC, dataInicio, dataFim });
                setShowGestorClientes(true);
              }}
            >
              Filtrar
            </button>
            <button
              type="button"
              style={clearBtn}
              onClick={() => {
                const fallbackGestor = isCurrentUserPrivileged ? 'Todos' : (gestorOptions[0] || 'Todos');
                setGestor(fallbackGestor);
                setClienteGC('Todos');
                setTipoContactoGC('Todos');
                setLocalidadeGC('Todos');
                setDataInicio('');
                setDataFim('');
                setSubmittedGC({ gestor: fallbackGestor, cliente: 'Todos', tipoContacto: 'Todos', localidade: 'Todos', dataInicio: '', dataFim: '' });
                setShowGestorClientes(true);
                setExpandedGestoresClientes({});
                setExpandedDatasGestorClientes({});
              }}
            >
              Limpar
            </button>
            <button
              type="button"
              style={exportBtn}
              onClick={exportGestorClientesToExcel}
              disabled={gestorClientesFiltrados.length === 0}
            >
              Exportar para Excel
            </button>
          </div>

          {showGestorClientes && (
          <div style={{ marginTop: 18 }}>
              <h3 style={reportHeading}>Clientes por Gestor</h3>
              {gestorClientesFiltrados.length === 0 ? (
                <p>Sem resultados para os filtros atuais.</p>
              ) : (
                <div style={listStyle}>
                  <div style={summaryBoxStyle}>
                    <div style={summaryTitleStyle}>Resumo Global</div>
                    <ResumoMetricsBlock resumo={resumoGlobalGestorClientes} />
                  </div>

                  <div style={summaryListSectionStyle}>
                    <div style={subHeadingStyle}>Sumários por Gestor</div>
                    {gestorClientesAgrupadosPorGestor.map((gestorGrupo) => (
                      <div key={`gc-summary-${gestorGrupo.gestorNome}`} style={summaryBoxStyle}>
                        <div style={summaryTitleStyle}>Sumário para Gestor: {gestorGrupo.gestorNome}</div>
                        <ResumoMetricsBlock resumo={gestorGrupo.resumo} />
                      </div>
                    ))}
                  </div>

                  <div style={detailReportsSectionStyle}>
                    <div style={subHeadingStyle}>Relatórios Detalhados</div>
                  {gestorClientesAgrupadosPorGestor.map((gestorGrupo) => {
                    const isGestorOpen = expandedGestoresClientes[gestorGrupo.gestorNome] ?? false;
                    return (
                      <div key={`gc-gestor-${gestorGrupo.gestorNome}`} style={dateSectionStyle}>
                        <button
                          type="button"
                          style={gestorToggleStyle}
                          onClick={() => {
                            // Tem de usar o mesmo "?? false" da leitura (isGestorOpen)
                            // acima — com "?? true" aqui, o primeiro clique não
                            // tinha efeito visível (false -> false) e só o segundo
                            // clique é que abria de facto a secção.
                            setExpandedGestoresClientes((prev) => ({
                              ...prev,
                              [gestorGrupo.gestorNome]: !(prev[gestorGrupo.gestorNome] ?? false)
                            }));
                          }}
                        >
                          <span style={gestorChevronStyle}>{isGestorOpen ? '▴' : '▾'}</span>
                          <span style={gestorHeadingStyle}>{`Gestor: ${gestorGrupo.gestorNome}`}</span>
                        </button>

                        {isGestorOpen && (
                          <div style={{ marginLeft: 0 }}>
                            {gestorGrupo.datas.map((grupo) => {
                              const isDateOpen = expandedDatasGestorClientes[`${gestorGrupo.gestorNome}::${grupo.dateKey}`] ?? false;
                              return (
                                <div key={`gc-data-${gestorGrupo.gestorNome}-${grupo.dateKey}`} style={dateSectionStyle}>
                                  <button
                                    type="button"
                                    style={dateToggleSimpleStyle}
                                    onClick={() => {
                                      // Mesmo "?? false" que isDateOpen usa na leitura —
                                      // ver comentário equivalente no toggle do gestor acima.
                                      setExpandedDatasGestorClientes((prev) => ({
                                        ...prev,
                                        [`${gestorGrupo.gestorNome}::${grupo.dateKey}`]: !(prev[`${gestorGrupo.gestorNome}::${grupo.dateKey}`] ?? false)
                                      }));
                                    }}
                                  >
                                    <span style={dateChevronSimpleStyle}>{isDateOpen ? '▴' : '▾'}</span>
                                    <span style={dateHeadingStyle}>{grupo.dateLabel}</span>
                                  </button>

                                  {isDateOpen && (
                                    <div style={listStyle}>
                                      {grupo.items.map((item) => (
                                        <div key={`gc-${item.fichaId}-${item.clienteId || item.clienteNome}`} style={detailCardStyle}>
                                          <div style={detailLineStyle}><strong>Ficha:</strong> {item.fichaTitulo}</div>
                                          <div style={detailLineStyle}><strong>Gestor:</strong> {item.gestorNome}</div>
                                          <div style={detailLineStyle}>
                                            <strong>Cliente:</strong>{' '}
                                            <button
                                              type="button"
                                              style={reportLinkStyle}
                                              onClick={() => {
                                                if (item.clienteId) navigate(`/clientes/${item.clienteId}/editar`);
                                              }}
                                              disabled={!item.clienteId}
                                              title={item.clienteId ? 'Editar cliente' : 'Cliente sem ligação'}
                                            >
                                              {item.clienteNome}
                                            </button>
                                          </div>
                                          <div style={detailLineStyle}><strong>Duração:</strong> {item.duracao}</div>
                                          <div style={detailLineStyle}><strong>Tipo de contacto:</strong> {item.tipoContacto}</div>
                                          <div style={detailLineStyle}><strong>Tipo de cliente:</strong> {item.tipoCliente}</div>
                                          <div style={detailLineStyle}><strong>Localidade:</strong> {item.localidade}</div>
                                          <div style={detailLineStyle}><strong>Contacto efetuado?</strong> {item.contactoEfetuado ? 'Sim' : 'Não'}</div>
                                          <div style={detailLineStyle}><strong>Motivo/Resumo do Contacto:</strong> {item.motivoResumo}</div>
                                          <div style={detailLineStyle}><strong>Motivo/Tipo do Próximo Contacto:</strong> {item.motivoTipoProximo}</div>
                                          <div style={detailLineStyle}><strong>Data do Próximo Contacto:</strong> {item.dataProximoContacto ? toDatePt(item.dataProximoContacto) : 'Sem data'}</div>
                                          <div style={detailLineStyle}><strong>Processo encerrado?</strong> {item.assuntoTratado ? 'Sim' : 'Não'}</div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tipo === 'clientes' && normalizedRole !== 'admin' && (
        <p style={{ marginTop: 18, color: '#b32d2e' }}>
          Não tens permissão para aceder a este relatório. Esta secção está
          disponível apenas para administradores.
        </p>
      )}

      {tipo === 'clientes' && normalizedRole === 'admin' && (
        <>
          <div style={rowStyle}>
            <label style={labelStyle}>Cliente</label>
            <select value={clienteFicha} onChange={(e) => setClienteFicha(e.target.value)} style={selectStyle}>
              {clienteOptions.map((c) => <option key={c}>{c}</option>)}
            </select>
            <button
              type="button"
              style={actionBtn}
              onClick={() => {
                setSubmittedClienteFicha(clienteFicha);
                setShowClienteFicha(true);
              }}
            >
              Ver Ficha
            </button>
            <button
              type="button"
              style={exportBtn}
              onClick={exportClienteFichaToExcel}
              disabled={!clienteFichaSelecionada}
            >
              Exportar para Excel
            </button>
          </div>

          {showClienteFicha && (
            <div style={{ marginTop: 18 }}>
              <h3 style={reportHeading}>Ficha Detalhada do Cliente</h3>
              {!clienteFichaSelecionada ? (
                <p>Seleciona um cliente e carrega em "Ver Ficha".</p>
              ) : (
                <ClienteFichaDetalhada ficha={clienteFichaSelecionada} onOpenFicha={(id) => navigate(`/fichas/${id}/editar`)} />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Uma mini-tabela rotulada (Label | Nº) — usada para as repartições por Tipo
// de Contacto / Tipo de Cliente. Substitui as "pílulas" soltas de antes por
// algo que se lê como uma grelha estruturada.
function MiniTable({ title, rows }) {
  const visibleRows = Object.entries(rows).filter(([, count]) => count > 0);
  if (visibleRows.length === 0) return null;

  return (
    <div style={miniTableBoxStyle}>
      <div style={miniTableTitleStyle}>{title}</div>
      <table style={miniTableStyle}>
        <tbody>
          {visibleRows.map(([label, count]) => (
            <tr key={label}>
              <td style={miniTableLabelCellStyle}>{label}</td>
              <td style={miniTableValueCellStyle}>{count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Bloco de métricas do resumo (usado tanto no Resumo Global como em cada
// Sumário por Gestor): 3 números-chave em destaque no topo, uma grelha
// estruturada com as restantes contagens, e duas mini-tabelas lado a lado
// para as repartições por Tipo de Contacto / Tipo de Cliente.
function ResumoMetricsBlock({ resumo }) {
  return (
    <div>
      <div style={resumoHeroGridStyle}>
        <div style={resumoHeroCardStyle}>
          <span style={resumoHeroValueStyle}>{resumo.total}</span>
          <span style={resumoHeroLabelStyle}>Total de Contactos</span>
        </div>
        <div style={resumoHeroCardStyle}>
          <span style={resumoHeroValueStyle}>{resumo.totalClientes}</span>
          <span style={resumoHeroLabelStyle}>Total de Clientes</span>
        </div>
        <div style={resumoHeroCardStyle}>
          <span style={resumoHeroValueStyle}>{resumo.realizados}</span>
          <span style={resumoHeroLabelStyle}>Contactos Realizados</span>
        </div>
      </div>

      <div style={summaryGridStyle}>
        <div style={summaryMetricCardStyle}><span style={summaryMetricLabelStyle}>Contactos Agendados</span><strong>{resumo.agendados}</strong></div>
        <div style={summaryMetricCardStyle}><span style={summaryMetricLabelStyle}>Follow Up</span><strong>{resumo.followUp}</strong></div>
        <div style={summaryMetricCardStyle}><span style={summaryMetricLabelStyle}>Reforços</span><strong>{resumo.reforcos}</strong></div>
        <div style={summaryMetricCardStyle}><span style={summaryMetricLabelStyle}>Novos Contactos</span><strong>{resumo.novos}</strong></div>
        <div style={summaryMetricCardStyle}><span style={summaryMetricLabelStyle}>Contactos Desmarcados</span><strong>{resumo.desmarcados}</strong></div>
      </div>

      <div style={miniTableWrapStyle}>
        <MiniTable title="Por Tipo de Contacto" rows={resumo.porTipoContacto} />
        <MiniTable title="Por Tipo de Cliente" rows={resumo.porTipoCliente} />
        <MiniTable title="Por Localidade" rows={resumo.porLocalidade} />
      </div>
    </div>
  );
}

// Toggle to reveal a proposal's free-text descriptive HTML — these blobs
// (copy-pasted from the original proposal documents) can run to hundreds of
// lines, so they stay collapsed by default rather than blowing up the card.
function DescritivoExpandivel({ html }) {
  const [open, setOpen] = useState(false);
  if (!html) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <button type="button" style={descritivoToggleStyle} onClick={() => setOpen((prev) => !prev)}>
        {open ? 'Ocultar descritivo' : 'Ver descritivo da proposta'}
      </button>
      {open && (
        <div style={descritivoBoxStyle} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
      )}
    </div>
  );
}

// Aba "Clientes" — "Ficha Detalhada do Cliente", no mesmo espírito do
// modelo clássico em papel: cabeçalho do cliente, histórico de contactos e
// orçamentos por estado comercial, com os totais em destaque.
function ClienteFichaDetalhada({ ficha, onOpenFicha }) {
  return (
    <div>
      <div style={summaryBoxStyle}>
        <div style={summaryTitleStyle}>{ficha.nome}</div>
        <div style={clienteHeaderGridStyle}>
          <div><span style={summaryMetricLabelStyle}>Gestor de Conta</span><div>{ficha.gestorNome}</div></div>
          <div><span style={summaryMetricLabelStyle}>Morada</span><div>{ficha.morada}</div></div>
          <div><span style={summaryMetricLabelStyle}>NIF</span><div>{ficha.nif}</div></div>
          <div><span style={summaryMetricLabelStyle}>Pessoa de Contacto</span><div>{ficha.pessoaContacto}{ficha.cargo !== '—' ? ` (${ficha.cargo})` : ''}</div></div>
          <div><span style={summaryMetricLabelStyle}>Telefone</span><div>{ficha.telefone}</div></div>
          <div><span style={summaryMetricLabelStyle}>Contacto (Tel./Email)</span><div>{ficha.contactoPessoa}</div></div>
        </div>
      </div>

      <div style={resumoHeroGridStyle}>
        <div style={resumoHeroCardStyle}>
          <span style={resumoHeroValueStyle}>{ficha.totais.contactos}</span>
          <span style={resumoHeroLabelStyle}>Total de Contactos</span>
        </div>
        <div style={resumoHeroCardStyle}>
          <span style={resumoHeroValueStyle}>{ficha.totais.orcamentos}</span>
          <span style={resumoHeroLabelStyle}>Total de Orçamentos</span>
        </div>
        <div style={resumoHeroCardStyle}>
          <span style={resumoHeroValueStyle}>{formatMoeda(ficha.totais.valorAprovados)}</span>
          <span style={resumoHeroLabelStyle}>Valor Aprovado</span>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={subHeadingStyle}>Histórico de Contactos</div>
        {ficha.historico.length === 0 ? (
          <p>Sem contactos registados para este cliente.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={servicosTableStyle}>
              <thead>
                <tr>
                  <th style={servicosThStyle}>Data</th>
                  <th style={servicosThStyle}>Gestor</th>
                  <th style={servicosThStyle}>Tipo</th>
                  <th style={servicosThStyle}>Novo?</th>
                  <th style={servicosThStyle}>Efetuado?</th>
                  <th style={servicosThStyle}>Descrição</th>
                </tr>
              </thead>
              <tbody>
                {ficha.historico.map((item) => (
                  <tr key={`hist-${item.fichaId}-${item.data}`}>
                    <td style={servicosTdStyle}>{toDatePt(item.data)}</td>
                    <td style={servicosTdStyle}>{item.gestorNome}</td>
                    <td style={servicosTdStyle}>{item.tipoContacto}{item.followUp ? ' · Follow Up' : ''}</td>
                    <td style={servicosTdStyle}>{item.novoContacto ? 'Sim' : 'Não'}</td>
                    <td style={servicosTdStyle}>{item.contactoEfetuado ? 'Sim' : 'Não'}</td>
                    <td style={servicosTdStyle}>
                      <button type="button" style={reportLinkStyle} onClick={() => onOpenFicha(item.fichaId)} title="Editar ficha">
                        {item.motivoResumo}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={subHeadingStyle}>Orçamentos e Estado Comercial</div>
        <div style={miniTableWrapStyle}>
          {ORCAMENTO_BUCKETS.map((bucket) => (
            <div key={bucket} style={miniTableBoxStyle}>
              <div style={miniTableTitleStyle}>{bucket} ({ficha.orcamentos[bucket].length})</div>
              {ficha.orcamentos[bucket].length === 0 ? (
                <div style={miniTableLabelCellStyle}>Sem registos</div>
              ) : (
                <table style={miniTableStyle}>
                  <tbody>
                    {ficha.orcamentos[bucket].map((o) => (
                      <tr key={`orc-${bucket}-${o.numero}`}>
                        <td style={miniTableLabelCellStyle}>
                          <button type="button" style={reportLinkStyle} onClick={() => onOpenFicha(o.numero)} title="Editar ficha">
                            Nº {o.numero} — {o.descricao}
                          </button>
                        </td>
                        <td style={miniTableValueCellStyle}>{formatMoeda(o.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Aba "Propostas" (Contacto Comercial): um cartão por ficha, agrupados por
// mês/ano de apresentação, cada um com a lista de serviços propostos e o
// valor total — layout de cartões para ser fácil de ler ficha a ficha.
function PropostasComerciais({
  grupos, totalCount, expandedMeses, setExpandedMeses,
  onOpenFicha, getFichaId, getClienteNome, getGestor, getPropostaMeta, isEstadoPlaceholder
}) {
  if (totalCount === 0) {
    return <p style={{ marginTop: 8 }}>Sem propostas com serviços preenchidos para os filtros atuais.</p>;
  }

  return (
    <div style={{ marginTop: 14 }}>
      {grupos.map((grupo) => (
        <div key={`prop-mes-${grupo.monthKey}`} style={dateSectionStyle}>
          <button
            type="button"
            style={dateToggleSimpleStyle}
            onClick={() => setExpandedMeses((prev) => ({ ...prev, [grupo.monthKey]: !(prev[grupo.monthKey] ?? true) }))}
          >
            <span style={dateChevronSimpleStyle}>{(expandedMeses[grupo.monthKey] ?? true) ? '▼' : '▶'}</span>
            <span style={dateHeadingStyle}>{grupo.monthLabel}</span>
            <span style={dateCountSimpleStyle}>({grupo.items.length})</span>
          </button>

          {(expandedMeses[grupo.monthKey] ?? true) && (
            <div style={propostaCardsGridStyle}>
              {grupo.items.map((ficha) => {
                const meta = getPropostaMeta(ficha) || {};
                const fichaId = getFichaId(ficha);
                return (
                  <div key={`prop-${fichaId}`} style={propostaCardStyle}>
                    <div style={propostaCardHeaderStyle}>
                      <button type="button" style={reportLinkStyle} onClick={() => onOpenFicha(fichaId)} title="Editar ficha">
                        {getClienteNome(ficha)}
                      </button>
                      {!isEstadoPlaceholder(meta.estado) && meta.estado && (
                        <span style={estadoBadgeStyle}>{meta.estado}</span>
                      )}
                    </div>
                    <div style={detailLineStyle}><strong>Gestor:</strong> {getGestor(ficha)}</div>
                    <div style={detailLineStyle}><strong>Data de apresentação:</strong> {meta.dataApresentacao ? toDatePt(meta.dataApresentacao) : 'Sem data'}</div>

                    {meta.servicos && meta.servicos.length > 0 && (
                      <table style={servicosTableStyle}>
                        <thead>
                          <tr><th style={servicosThStyle}>Serviço</th><th style={servicosThStyle}>Valor</th></tr>
                        </thead>
                        <tbody>
                          {meta.servicos.map((servico, idx) => (
                            <tr key={`${fichaId}-servico-${idx}`}>
                              <td style={servicosTdStyle}>{servico.servico || '—'}</td>
                              <td style={servicosTdStyle}>{servico.valor || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    <div style={propostaTotalStyle}>Valor total: <strong>{formatMoeda(meta.valorTotal)}</strong></div>

                    <DescritivoExpandivel html={meta.descritivo} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Aba "Propostas Adjudicadas" (Contacto Financeiro): agrupado por cliente
// com subtotal, um banner de total consolidado no topo, e uma tabela por
// cliente com os serviços adjudicados e dados de faturação de cada ficha.
function PropostasAdjudicadasFinanceiro({
  grupos, totalCount, expandedMeses, setExpandedMeses,
  onOpenFicha, getFichaId, getClienteNome, getGestor, getAdjudicadaMeta
}) {
  if (totalCount === 0) {
    return <p style={{ marginTop: 8 }}>Sem propostas adjudicadas com serviços ou faturação preenchidos para os filtros atuais.</p>;
  }

  return (
    <div style={{ marginTop: 14 }}>
      {grupos.map((grupo) => {
        const subtotal = grupo.items.reduce((sum, ficha) => sum + (parseMoeda(getAdjudicadaMeta(ficha)?.valorTotal) || 0), 0);
        return (
          <div key={`adj-mes-${grupo.monthKey}`} style={dateSectionStyle}>
            <button
              type="button"
              style={dateToggleSimpleStyle}
              onClick={() => setExpandedMeses((prev) => ({ ...prev, [grupo.monthKey]: !(prev[grupo.monthKey] ?? true) }))}
            >
              <span style={dateChevronSimpleStyle}>{(expandedMeses[grupo.monthKey] ?? true) ? '▼' : '▶'}</span>
              <span style={dateHeadingStyle}>{grupo.monthLabel}</span>
              <span style={dateCountSimpleStyle}>({grupo.items.length}) — {formatMoeda(subtotal)}</span>
            </button>

            {(expandedMeses[grupo.monthKey] ?? true) && (
              <div style={{ overflowX: 'auto' }}>
                <table style={adjudicadaTableStyle}>
                  <thead>
                    <tr>
                      <th style={servicosThStyle}>Cliente</th>
                      <th style={servicosThStyle}>Gestor</th>
                      <th style={servicosThStyle}>Serviços adjudicados</th>
                      <th style={servicosThStyle}>Valor total</th>
                      <th style={servicosThStyle}>Faturação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.items.map((ficha) => {
                      const meta = getAdjudicadaMeta(ficha) || {};
                      const fichaId = getFichaId(ficha);
                      return (
                        <tr key={`adj-${fichaId}`}>
                          <td style={servicosTdStyle}>
                            <button type="button" style={reportLinkStyle} onClick={() => onOpenFicha(fichaId)} title="Editar ficha">
                              {getClienteNome(ficha)}
                            </button>
                          </td>
                          <td style={servicosTdStyle}>{getGestor(ficha)}</td>
                          <td style={servicosTdStyle}>
                            {meta.servicos && meta.servicos.length > 0
                              ? meta.servicos.map((servico) => servico.servico).filter(Boolean).join(', ')
                              : '—'}
                          </td>
                          <td style={servicosTdStyle}><strong>{formatMoeda(meta.valorTotal)}</strong></td>
                          <td style={servicosTdStyle}>
                            {(() => {
                              const valorFatura = parseMoeda(meta.valorFatura);
                              if (valorFatura === null && !meta.dataFatura) return '—';
                              if (valorFatura === null) return `Faturado em ${toDatePt(meta.dataFatura)}`;
                              return `${formatMoeda(meta.valorFatura)}${meta.dataFatura ? ' em ' + toDatePt(meta.dataFatura) : ''}`;
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const pageStyle = { fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', fontSize: '0.93rem', color: '#1d2327' };
const rowStyle = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 };
const labelStyle = { fontWeight: 600, fontSize: '0.88rem', color: '#1d2327', minWidth: 50 };
const selectStyle = { border: '1px solid #c3c4c7', borderRadius: 3, padding: '5px 8px', fontSize: '0.9rem', minWidth: 260 };
const dateInputStyle = { border: '1px solid #c3c4c7', borderRadius: 3, padding: '5px 8px', fontSize: '0.9rem' };
const actionBtn = { background: '#f6f7f7', border: '1px solid #c3c4c7', borderRadius: 3, padding: '5px 12px', fontSize: '0.88rem', cursor: 'pointer', color: '#1d2327', whiteSpace: 'nowrap', flexShrink: 0 };
const exportBtn = { background: '#8aaE8c', border: '1px solid #8aaE8c', borderRadius: 3, padding: '5px 12px', fontSize: '0.88rem', cursor: 'pointer', color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 };
const clearBtn = { background: 'none', border: 'none', color: 'var(--theme-topbar-start, #2271b1)', fontSize: '0.88rem', cursor: 'pointer', padding: '5px 0', whiteSpace: 'nowrap', flexShrink: 0 };
const reportHeading = { fontWeight: 700, fontSize: '1.7rem', margin: '2px 0 12px' };
const searchInputStyle = { border: '1px solid #c3c4c7', borderRadius: 3, padding: '5px 8px', fontSize: '0.88rem', minWidth: 300 };
const listStyle = { display: 'flex', flexDirection: 'column', gap: 7 };
const dateSectionStyle = { marginBottom: 8 };
const summaryBoxStyle = { padding: '6px 0 10px' };
const summaryTitleStyle = { fontSize: '1.1rem', fontWeight: 500, marginBottom: 6 };
const summaryGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 };
const summaryMetricCardStyle = { background: '#f6f8fb', border: '1px solid #dce3ea', borderRadius: 8, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const summaryMetricLabelStyle = { color: '#4b5563', fontSize: '0.88rem' };
// Cartões-destaque do Resumo (Total de Contactos / Total de Clientes / Realizados)
const resumoHeroGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 10 };
const resumoHeroCardStyle = { background: '#fff', border: '1px solid #dce3ea', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 };
const resumoHeroValueStyle = { fontSize: '1.9rem', fontWeight: 700, color: '#1d2327', lineHeight: 1 };
const resumoHeroLabelStyle = { fontSize: '0.8rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' };

// Cabeçalho da "Ficha Detalhada do Cliente"
const clienteHeaderGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px 20px', marginTop: 10, fontSize: '0.92rem' };

// Mini-tabelas "Por Tipo de Contacto" / "Por Tipo de Cliente"
const miniTableWrapStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginTop: 12 };
const miniTableBoxStyle = { border: '1px solid #dce3ea', borderRadius: 8, overflow: 'hidden', background: '#fff' };
const miniTableTitleStyle = { background: '#eef2f7', padding: '7px 12px', fontWeight: 600, fontSize: '0.85rem', color: '#374151', borderBottom: '1px solid #dce3ea' };
const miniTableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' };
const miniTableLabelCellStyle = { padding: '6px 12px', borderTop: '1px solid #f0f1f2', color: '#374151' };
const miniTableValueCellStyle = { padding: '6px 12px', borderTop: '1px solid #f0f1f2', textAlign: 'right', fontWeight: 700, color: '#1d2327' };
const summaryListSectionStyle = { marginTop: 10 };
const detailReportsSectionStyle = { marginTop: 16, paddingTop: 10, borderTop: '1px solid #e5e7eb' };
const subHeadingStyle = { fontSize: '1.02rem', fontWeight: 600, color: '#374151', marginBottom: 6 };
const gestorToggleStyle = { background: 'none', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 8, padding: 0, margin: '10px 0 4px', cursor: 'pointer' };
const gestorChevronStyle = { color: '#1d2327', fontSize: '0.9rem', transform: 'translateY(-1px)' };
const gestorHeadingStyle = { fontSize: '1.15rem', fontWeight: 700, color: '#1d2327' };
const dateToggleSimpleStyle = { background: 'none', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0, margin: '8px 0 6px', cursor: 'pointer' };
const dateChevronSimpleStyle = { color: '#6b7280', fontSize: '0.82rem', transform: 'translateY(-1px)' };
const dateHeadingStyle = { fontSize: '1rem', fontWeight: 600, color: '#3c434a' };
const dateCountSimpleStyle = { color: '#6b7280', fontSize: '0.9rem' };
const detailCardStyle = { borderTop: '1px solid #e5e7eb', padding: '10px 0' };
const detailLineStyle = { fontSize: '1.02rem', marginBottom: 4, lineHeight: 1.5 };
const reportLinkStyle = { background: 'none', border: 'none', color: '#005eff', padding: 0, cursor: 'pointer', textAlign: 'left', fontSize: '1.05rem' };
const gcGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(170px, 1fr))', gap: '10px 16px', alignItems: 'start' };
const gcFieldStyle = { display: 'flex', flexDirection: 'column', minWidth: 0 };
const gcLabelStyle = { ...labelStyle, display: 'block', minWidth: 0, marginBottom: 6 };
const gcControlStyle = {
  border: '1px solid #c3c4c7',
  borderRadius: 3,
  padding: '5px 8px',
  fontSize: '0.9rem',
  height: 40,
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  background: '#fff'
};

// Aba "Propostas" — cartões
const propostaCardsGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12, marginTop: 4 };
const propostaCardStyle = { background: '#fff', border: '1px solid #dce3ea', borderRadius: 8, padding: '12px 14px' };
const propostaCardHeaderStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 };
const estadoBadgeStyle = { background: '#eef2ff', color: '#3730a3', fontSize: '0.78rem', fontWeight: 600, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' };
const propostaTotalStyle = { marginTop: 8, fontSize: '1rem', color: '#1d2327' };
const descritivoToggleStyle = { ...clearBtn, padding: 0, marginTop: 4 };
const descritivoBoxStyle = { marginTop: 6, padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: '0.85rem', lineHeight: 1.5, maxHeight: 320, overflowY: 'auto' };

// Tabelas de serviços (partilhadas entre Propostas e Propostas Adjudicadas)
const servicosTableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: '0.88rem' };
const servicosThStyle = { textAlign: 'left', borderBottom: '1px solid #dce3ea', padding: '4px 6px', color: '#4b5563', fontWeight: 600 };
const servicosTdStyle = { borderBottom: '1px solid #f0f1f2', padding: '4px 6px' };

// Aba "Propostas Adjudicadas" — tabela por mês
const adjudicadaTableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: 4, fontSize: '0.9rem', minWidth: 640 };
